import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const TEMPS = [
  { key: "Hot", label: "Hot leads", tone: "border-rose-200 bg-rose-50/40", dot: "bg-rose-500" },
  { key: "Warm", label: "Warm leads", tone: "border-amber-200 bg-amber-50/40", dot: "bg-amber-500" },
  { key: "Cool", label: "Cool leads", tone: "border-sky-200 bg-sky-50/40", dot: "bg-sky-500" },
  { key: "Cold", label: "Cold leads", tone: "border-slate-200 bg-slate-50/50", dot: "bg-slate-400" },
];

const CHANNELS = [
  { v: "call", l: "Call" },
  { v: "whatsapp", l: "WhatsApp" },
  { v: "sms", l: "SMS" },
];

const fmtFirst = (m) => {
  const n = parseInt(m, 10) || 0;
  if (n >= 60) {
    const h = n / 60;
    return `${h % 1 === 0 ? h : h.toFixed(1)}h`;
  }
  return `${n}m`;
};

const CH_SHORT = { call: "Call", whatsapp: "WA", sms: "SMS" };

// Mirror of backend cadence.generate_rules_from_knobs — for live preview only.
function genSchedule(k) {
  const frH = Math.max(0, (parseInt(k.first_response_minutes, 10) || 0) / 60);
  const a1 = Math.min(8, Math.max(1, parseInt(k.attempts_day1, 10) || 1));
  const totalDays = Math.min(30, Math.max(1, parseInt(k.total_days, 10) || 1));
  const primary = k.primary_channel || "call";
  const alt = primary !== "whatsapp" ? "whatsapp" : "call";
  const rules = [];
  let offs;
  if (a1 === 1) {
    offs = [frH];
  } else {
    const last = 22 > frH ? 22 : frH + 2 * (a1 - 1);
    const step = (last - frH) / (a1 - 1);
    offs = Array.from({ length: a1 }, (_, i) => Math.round((frH + i * step) * 100) / 100);
  }
  offs.forEach((off, i) => rules.push([off, i % 2 === 0 ? primary : alt]));
  for (let d = 2; d <= totalDays; d++) {
    rules.push([(d - 1) * 24 + 4, d % 2 === 0 ? primary : alt]);
  }
  rules.sort((a, b) => a[0] - b[0]);
  return rules;
}

const offLabel = (off) => {
  if (off < 1) return `${Math.round(off * 60)}m`;
  if (off < 24) return `${Math.round(off)}h`;
  return `D${Math.floor(off / 24) + 1}`;
};

export default function SlaSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [knobs, setKnobs] = useState(null);
  const [configured, setConfigured] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sla-config");
      setKnobs(data.knobs);
      setConfigured(data.configured);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (temp, field, value) =>
    setKnobs((k) => ({ ...k, [temp]: { ...k[temp], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { knobs: {} };
      for (const t of TEMPS) {
        const k = knobs[t.key] || {};
        payload.knobs[t.key] = {
          first_response_minutes: Math.max(0, parseInt(k.first_response_minutes, 10) || 0),
          attempts_day1: Math.min(8, Math.max(1, parseInt(k.attempts_day1, 10) || 1)),
          total_days: Math.min(30, Math.max(1, parseInt(k.total_days, 10) || 1)),
          primary_channel: k.primary_channel || "call",
        };
      }
      await api.put("/admin/sla-config", payload);
      toast.success("SLA targets saved — applies to your tenant immediately");
      setConfigured(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await api.delete("/admin/sla-config");
      toast.success("Reset to recommended defaults");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !knobs) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="sla-settings-tab">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-xs text-slate-500">
          Tune how aggressively your team chases each lead temperature. The
          <span className="font-medium text-[#0A2540]"> first-response target</span> is your
          Speed-to-Lead SLA; attempts &amp; follow-up days drive the persistence SLA. These power
          the Overdue badges, SLA dashboard and the cadence tasks your counsellors see.
          {configured ? "" : " (Currently using recommended defaults.)"}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={reset} data-testid="sla-reset">
            <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="sla-save"
          >
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save targets
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {TEMPS.map((t) => {
          const k = knobs[t.key] || {};
          const touches =
            (parseInt(k.attempts_day1, 10) || 1) +
            Math.max(0, (parseInt(k.total_days, 10) || 1) - 1);
          return (
            <div key={t.key} className={`rounded-xl border p-5 ${t.tone}`} data-testid={`sla-card-${t.key}`}>
              <div className="mb-4 flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${t.dot}`} />
                <h3 className="font-display font-semibold text-[#0A2540]">{t.label}</h3>
                <span className="ml-auto text-[11px] text-slate-500">
                  ≈ {touches} touches / {k.total_days} days
                </span>
              </div>
              <div className="space-y-3.5">
                <div>
                  <Label className="text-xs text-slate-600">First response within (minutes)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={k.first_response_minutes ?? ""}
                    onChange={(e) => update(t.key, "first_response_minutes", e.target.value)}
                    className="mt-1"
                    data-testid={`sla-${t.key}-first`}
                  />
                  <p className="mt-1 text-[10px] text-slate-400">
                    Speed-to-lead target — {fmtFirst(k.first_response_minutes)} after the lead arrives
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Attempts on day 1</Label>
                    <Input
                      type="number"
                      min={1}
                      max={8}
                      value={k.attempts_day1 ?? ""}
                      onChange={(e) => update(t.key, "attempts_day1", e.target.value)}
                      className="mt-1"
                      data-testid={`sla-${t.key}-attempts`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Total follow-up days</Label>
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={k.total_days ?? ""}
                      onChange={(e) => update(t.key, "total_days", e.target.value)}
                      className="mt-1"
                      data-testid={`sla-${t.key}-days`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Primary channel</Label>
                  <Select
                    value={k.primary_channel}
                    onValueChange={(v) => update(t.key, "primary_channel", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid={`sla-${t.key}-channel`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map((c) => (
                        <SelectItem key={c.v} value={c.v}>
                          {c.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {(() => {
                  const sched = genSchedule(k);
                  const shown = sched.slice(0, 5);
                  return (
                    <div
                      className="rounded-lg border border-slate-200 bg-white/70 px-2.5 py-2"
                      data-testid={`sla-${t.key}-preview`}
                    >
                      <div className="mb-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Schedule preview
                      </div>
                      <div className="flex flex-wrap items-center gap-1 text-[10px] text-slate-600">
                        {shown.map(([off, ch], i) => (
                          <span key={i} className="inline-flex items-center">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono">
                              {offLabel(off)}·{CH_SHORT[ch]}
                            </span>
                            {i < shown.length - 1 && <span className="mx-0.5 text-slate-300">→</span>}
                          </span>
                        ))}
                        {sched.length > shown.length && (
                          <span className="text-slate-400">+{sched.length - shown.length} more</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
